import { useCallback, useEffect, useState } from 'react';
import {
  ShoppingBasket, Check, Plus, Trash2, Undo2, Sparkles, Loader2,
} from 'lucide-react';
import { api } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import EstadoVacio from '../ui/EstadoVacio';
import ModalConfirmar from '../ui/ModalConfirmar';

// LA PANTALLA QUE LE FALTABA A UN FLUJO YA CONSTRUIDO.
//
// La lista de compras existe en la base y en la API desde la Fase 1, y se
// puebla sola: cuando algo de casa baja de su mínimo, TEGO lo apunta aquí
// (`inventario._evaluar_alerta_stock`). Hasta ahora no había forma de
// abrirla. Es decir: el sistema apuntaba lo que hacía falta comprar en un
// sitio al que nadie podía entrar — que es peor que no apuntarlo, porque
// desde dentro parecía que el aviso funcionaba.
//
// TRES DECISIONES DE LA PANTALLA, y las tres son de usuario cero:
//
//   1. LO COMPRADO NO DESAPARECE. Marcar algo por error es el error más
//      probable aquí —se hace con el pulgar, con prisa, en el súper— así
//      que lo comprado se queda a un toque de volver. Un "deshacer" que
//      dura tres segundos en un aviso no sirve a quien se dio cuenta al
//      llegar a la caja.
//   2. SE PUEDE APUNTAR CUALQUIER COSA, exista o no en el inventario.
//      Quien está haciendo la lista del súper escribe "pilas AA", no un
//      SKU. Si además coincide con algo del catálogo, mejor; si no,
//      también sirve. Un solo campo obligatorio.
//   3. SE DICE QUIÉN LO APUNTÓ. Lo que puso TEGO solo lleva su marca y
//      explica por qué está ahí. Sin eso, aparecen renglones que nadie
//      recuerda haber escrito y la lista deja de ser de fiar.

const LIMPIO = { texto: '', cantidad: '' };

export default function ListaCompras() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [nuevo, setNuevo] = useState(LIMPIO);
  const [guardando, setGuardando] = useState(false);
  const [verComprados, setVerComprados] = useState(false);
  const [aBorrar, setABorrar] = useState(null);
  const avisos = useAvisos();

  // Se piden TAMBIÉN los comprados: son los que permiten deshacer. Traerlos
  // en la misma petición evita una segunda vuelta al servidor cuando
  // alguien despliega la sección, que es justo cuando tiene prisa.
  const cargar = useCallback(async () => {
    try {
      setItems(await api.get('/lista-compras?incluir_comprados=true'));
      setError('');
    } catch (e) {
      setError(e.message || 'No se pudo cargar la lista.');
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function agregar(evento) {
    evento.preventDefault();
    const texto = nuevo.texto.trim();
    if (!texto) return;
    setGuardando(true);
    try {
      // Se manda como texto libre siempre. Intentar adivinar si lo escrito
      // es un SKU haría que "pilas AA" fallara con un 404 hablando de
      // códigos de producto, que a quien hace la lista del súper no le dice
      // absolutamente nada.
      await api.post('/lista-compras', {
        texto_libre: texto,
        cantidad_sugerida: nuevo.cantidad ? Number(nuevo.cantidad) : null,
      });
      setNuevo(LIMPIO);
      await cargar();
      avisos.exito(`«${texto}» quedó en la lista.`);
    } catch (e) {
      avisos.error(e.message || 'No se pudo agregar.');
    } finally {
      setGuardando(false);
    }
  }

  async function marcar(item, comprado) {
    // Optimista: en el pasillo del súper, esperar al servidor para tachar
    // algo se siente roto. Si falla, se recarga y se dice.
    setItems((lista) => lista.map((i) => (i.id === item.id ? { ...i, comprado } : i)));
    try {
      await api.patch(`/lista-compras/${item.id}`, { comprado });
      await cargar();
    } catch (e) {
      await cargar();
      avisos.error(e.message || 'No se pudo guardar el cambio.');
    }
  }

  // NO cierra el modal ni traga el error: `ModalConfirmar` cierra solo
  // cuando esto termina bien, y pinta el fallo dentro si algo revienta.
  // Cerrarlo aquí lo desmontaría a mitad de la petición y el error se
  // quedaría sin sitio donde mostrarse.
  async function borrar(item) {
    await api.del(`/lista-compras/${item.id}`);
    await cargar();
    avisos.exito('Quitado de la lista.');
  }

  const pendientes = (items || []).filter((i) => !i.comprado);
  const comprados = (items || []).filter((i) => i.comprado);

  return (
    <div className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-gray-900">Lista de compras</h2>
        <p className="text-sm text-gray-600 mt-0.5">
          Lo que hay que reponer. TEGO apunta solo lo que se está acabando, y tú
          puedes agregar lo que quieras.
        </p>
      </header>

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-800 rounded-xl p-3 text-sm">
          {error} Vuelve a intentarlo; si sigue igual, avisa a quien administra TEGO.
        </div>
      )}

      {/* Agregar va ARRIBA y siempre visible: es la acción que trae a
          alguien a esta pantalla cuando se acuerda de algo, y esconderla
          tras un botón «+» obliga a un toque de más con el carrito en la
          otra mano. */}
      <form onSubmit={agregar} className="flex flex-col sm:flex-row gap-2">
        <label className="sr-only" htmlFor="que-falta">Qué falta</label>
        <input
          id="que-falta"
          value={nuevo.texto}
          onChange={(e) => setNuevo({ ...nuevo, texto: e.target.value })}
          placeholder="¿Qué falta? Por ejemplo: pilas AA"
          maxLength={200}
          className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-base
                     focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
        />
        <label className="sr-only" htmlFor="cuantas">Cuántas (opcional)</label>
        <input
          id="cuantas"
          value={nuevo.cantidad}
          onChange={(e) => setNuevo({ ...nuevo, cantidad: e.target.value })}
          placeholder="Cuántas"
          inputMode="decimal"
          className="sm:w-28 border border-gray-300 rounded-xl px-4 py-3 text-base
                     focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
        />
        <button
          type="submit"
          disabled={!nuevo.texto.trim() || guardando}
          // El botón deshabilitado EXPLICA por qué, en vez de quedarse gris
          // y mudo: sin esto, quien no escribió nada cree que está roto.
          title={!nuevo.texto.trim() ? 'Escribe primero qué falta' : 'Agregar a la lista'}
          className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3
                     font-semibold text-white bg-[var(--color-marca)]
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {guardando
            ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            : <Plus className="h-5 w-5" aria-hidden="true" />}
          Agregar
        </button>
      </form>
      {!nuevo.texto.trim() && (
        <p className="text-xs text-gray-500 -mt-3">
          Escribe qué falta para poder agregarlo. La cantidad es opcional.
        </p>
      )}

      {items === null && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando la lista…
        </div>
      )}

      {items !== null && pendientes.length === 0 && (
        <EstadoVacio
          icono={ShoppingBasket}
          titulo="No falta nada"
          descripcion={
            comprados.length > 0
              ? 'Ya compraste todo lo que estaba apuntado. Cuando algo de casa baje de su mínimo, TEGO lo apuntará aquí solo.'
              : 'Cuando algo baje de su mínimo, TEGO lo apuntará aquí solo. También puedes agregar lo que quieras arriba.'
          }
        />
      )}

      {pendientes.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-bold text-gray-500 uppercase">
            Por comprar ({pendientes.length})
          </h3>
          {pendientes.map((i) => (
            <Renglon
              key={i.id}
              item={i}
              onMarcar={() => marcar(i, true)}
              onBorrar={() => setABorrar(i)}
            />
          ))}
        </section>
      )}

      {comprados.length > 0 && (
        <section className="space-y-2">
          <button
            onClick={() => setVerComprados((v) => !v)}
            className="text-xs font-bold text-gray-500 uppercase inline-flex items-center gap-1.5"
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            Ya compré ({comprados.length}) {verComprados ? '· ocultar' : '· ver'}
          </button>
          {verComprados && comprados.map((i) => (
            <Renglon
              key={i.id}
              item={i}
              onMarcar={() => marcar(i, false)}
              onBorrar={() => setABorrar(i)}
            />
          ))}
        </section>
      )}

      {aBorrar && (
        <ModalConfirmar
          titulo="¿Quitar de la lista?"
          descripcion={`«${nombreDe(aBorrar)}» desaparecerá de la lista. Si vuelve a bajar de su mínimo, TEGO lo apuntará otra vez.`}
          etiquetaConfirmar="Sí, quitar"
          onConfirmar={() => borrar(aBorrar)}
          onCerrar={() => setABorrar(null)}
        />
      )}
    </div>
  );
}

function nombreDe(item) {
  return item.producto || item.texto_libre || 'Este renglón';
}

function Renglon({ item, onMarcar, onBorrar }) {
  const comprado = item.comprado;
  const automatico = item.origen === 'AUTO';
  return (
    <div className={`border rounded-xl p-3 flex items-center gap-3 ${
      comprado ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}
    >
      {/* Objetivo táctil grande y a la izquierda: se usa con el pulgar,
          con una mano, delante del estante. */}
      <button
        onClick={onMarcar}
        aria-label={comprado ? `Devolver ${nombreDe(item)} a la lista` : `Marcar ${nombreDe(item)} como comprado`}
        className={`shrink-0 h-11 w-11 rounded-xl border flex items-center justify-center ${
          comprado
            ? 'bg-green-600 border-green-600 text-white'
            : 'border-gray-300 text-gray-400 hover:border-green-500 hover:text-green-600'}`}
      >
        {comprado ? <Undo2 className="h-5 w-5" /> : <Check className="h-5 w-5" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className={`font-medium truncate ${comprado ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
          {nombreDe(item)}
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
          {item.sku && <span className="font-mono">{item.sku}</span>}
          {item.cantidad_sugerida != null && (
            <span className="tabular-nums">Llevar {Number(item.cantidad_sugerida)}</span>
          )}
          {automatico && (
            // POR QUÉ ESTÁ AQUÍ. Sin esta línea aparecen renglones que
            // nadie recuerda haber escrito, y la lista deja de creerse.
            <span className="inline-flex items-center gap-1 text-[var(--color-marca)]">
              <Sparkles className="h-3 w-3" aria-hidden="true" /> Lo apuntó TEGO: se está acabando
            </span>
          )}
        </div>
      </div>

      <button
        onClick={onBorrar}
        aria-label={`Quitar ${nombreDe(item)} de la lista`}
        title="Quitar de la lista"
        className="shrink-0 h-11 w-11 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50
                   flex items-center justify-center"
      >
        <Trash2 className="h-5 w-5" />
      </button>
    </div>
  );
}
